import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DATE_FORMAT } from "@/constants/datetime";
import { MessageBox } from "@/features/chat/components/MessageBox";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { useDialog } from "@/hooks/useDialog";
import { useMount } from "@/hooks/useMount";
import { text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

export const Room = () => {
  const { currentRoom } = useChat();
  const isMounted = useMount();
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const confirmModal = useDialog();

  const isRomDeleted = useMemo(() => !!currentRoom.roomData?.creator.deletedAt, [currentRoom]);
  const isMatchDeleted = useMemo(() => !!currentRoom.roomData?.match?.deletedAt, [currentRoom]);

  const handleRemoveMessage = async (id: number) => {
    try {
      const agree = await confirmModal.show();
      if (!agree) return;
      await currentRoom?.deleteMsg(id);
    } catch (error) {
      showError(error);
    }
  };

  const handleScroll = async () => {
    if (!currentRoom.roomRef.current || isFetchingMore || !currentRoom.hasNextPage) return;

    if (currentRoom.roomRef.current?.scrollTop === 0) {
      setIsFetchingMore(true);
      await currentRoom.fetchNextPage();
      setTimeout(() => {
        setIsFetchingMore(false);
      }, 200);
    }
  };

  useEffect(() => {
    if (!isMounted) return;

    const scrollContainer = currentRoom.roomRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [isMounted, currentRoom, isFetchingMore]);

  const mergedMessages = useMemo(() => [...currentRoom.oldMessages, ...currentRoom.newMessages], [currentRoom.oldMessages, currentRoom.newMessages]);
  const isExistMessageList = mergedMessages.length > 0;

  // Scroll to bottom on initial mount
  useEffect(() => {
    if (!isMounted && !isExistMessageList) return;
    currentRoom.scrollToBottom();
  }, [isMounted, isExistMessageList]);

  // Render messages with date separators
  const messageListContent = mergedMessages.map((item, index, arr) => {
    const currentDate = item.createdAt ? dayjs(item.createdAt).format(DATE_FORMAT) : null;
    const prevDate = index > 0 && arr[index - 1].createdAt ? dayjs(arr[index - 1].createdAt).format(DATE_FORMAT) : null;

    return (
      <>
        {currentDate && currentDate !== prevDate && (
          <Typography component="p" my={2} textAlign="center" variant="sub16Semi" color={text.secondary}>
            {currentDate}
          </Typography>
        )}
        <MessageBox key={item.id} {...item} handleRemoveMessage={handleRemoveMessage} />
      </>
    );
  });

  // Notice shown at the end of the message list when the match (referral company) has been deleted.
  // Use visibility:hidden (not conditional render) while messages are loading so the notice
  // doesn't flash above the spinner, but its layout slot is reserved from the start. This
  // keeps scrollHeight stable across the loading→loaded transition, so scrollToBottom's
  // initial scroll lands at a position that still has the notice in view once it appears.
  const matchDeletedNotice = isMatchDeleted && (
    <Typography
      component="p"
      my={2}
      textAlign="center"
      variant="sub16Semi"
      color={text.secondary}
      sx={{ visibility: currentRoom.isFetching ? "hidden" : "visible" }}
    >
      紹介会社が既に削除されました。
    </Typography>
  );

  // Notice shown in place of messages when the room creator (user) has been deleted
  const userDeletedNotice = (
    <Typography component="p" my={2} textAlign="center" variant="sub16Semi" color={text.secondary}>
      ユーザーが削除されました。
    </Typography>
  );

  return (
    <>
      <RoomWrapper ref={currentRoom.roomRef}>
        {(currentRoom.isFetching || isFetchingMore) && (
          <SpinnerContainer>
            <CircularProgress color={"primary"} />
          </SpinnerContainer>
        )}
        {isRomDeleted ? (
          userDeletedNotice
        ) : (
          <>
            {messageListContent}
            {matchDeletedNotice}
          </>
        )}
      </RoomWrapper>

      <ConfirmSaveModal open={confirmModal.open} onOk={confirmModal.confirm} onClose={confirmModal.cancel}>
        <Typography>削除すると元に戻せません。削除してもよろしいですか？</Typography>
      </ConfirmSaveModal>
    </>
  );
};

const RoomWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: white;
  flex: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 60px;
`;

const SpinnerContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;
